
import type { SchoolTask, AdminTask, CustomTask, UserTask } from '@/types/tasks'

interface DiscoverTask {
  id: string
  title: string
  description: string | null
  module: string | null
  domain: string | null
  priority: boolean
  source: 'admin' | 'school'
}

export const getDiscoverTasks = (schoolTasks: SchoolTask[], adminTasks: AdminTask[], userTasks: UserTask[]) => {
  const userTaskIds = new Set(userTasks.map(ut => ut.task_id))
  
  // Filter out tasks that are already in user's task list
  const availableSchoolTasks = schoolTasks.filter(task => !userTaskIds.has(task.id))
  const availableAdminTasks = adminTasks.filter(task => !userTaskIds.has(task.id))
  
  // Transform and combine tasks with proper source typing
  const schoolDiscoverTasks: DiscoverTask[] = availableSchoolTasks.map(task => ({
    ...task,
    source: 'school' as const
  }))
  
  const adminDiscoverTasks: DiscoverTask[] = availableAdminTasks.map(task => ({
    ...task,
    source: 'admin' as const
  }))
  
  // Prioritize admin tasks first, then school tasks
  // Within each group, sort by priority and creation date
  const sortedAdminTasks = adminDiscoverTasks.sort((a, b) => {
    if (a.priority !== b.priority) return b.priority ? 1 : -1
    return new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
  })
  
  const sortedSchoolTasks = schoolDiscoverTasks.sort((a, b) => {
    if (a.priority !== b.priority) return b.priority ? 1 : -1
    return new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
  })
  
  return [...sortedAdminTasks, ...sortedSchoolTasks]
}

export const getMyTasks = (userTasks: UserTask[], customTasks: CustomTask[]) => {
  const activeTasks = userTasks.filter(task => task.status === 'active')
  const activeCustomTasks = customTasks.filter(task => task.status === 'active')
  
  return {
    userTasks: activeTasks,
    customTasks: activeCustomTasks
  }
}

export const getPriorityTasks = (userTasks: UserTask[], customTasks: CustomTask[]) => {
  const priorityUserTasks = userTasks.filter(task => 
    task.status === 'active' && task.school_tasks?.priority
  )
  
  // Only show custom tasks that are actually marked as priority
  const priorityCustomTasks = customTasks.filter(task => task.status === 'priority')
  
  return {
    userTasks: priorityUserTasks,
    customTasks: priorityCustomTasks
  }
}

export const getArchivedTasks = (userTasks: UserTask[], customTasks: CustomTask[]) => {
  const archivedUserTasks = userTasks.filter(task => task.status === 'archived')
  const archivedCustomTasks = customTasks.filter(task => task.status === 'archived')
  
  return {
    userTasks: archivedUserTasks,
    customTasks: archivedCustomTasks
  }
}
